import * as d3 from 'd3';
import { defineTexturedGlassPatterns } from './glassPatternsDefs';

export function drawArchGridShape(
  svg,
  initX,
  initY,                 // Top of the arch
  w,
  hRect,                 // Rectangle height (can be 0)
  archHeight,            // Height of arch (vertical radius)
  frameWidth = 10,
  frameColor = 'white',
  lineFrameColor = 'black',
  outerStrokeColor = 'black',
  outerStrokeWidth = 1,
  color = '#EDF9FF',     // Default glass color
  includeInnerTopLine = false,
  glassType = null,      // 'Tinted' | 'Textured' | null
  glassDesign = null,    // e.g. 'Grey', 'Rain blast', etc.
  colNumber = 0,         // Number of sections (0 if only 1 section)
  sectionWidths = [],    // Array of actual section widths
  doorData = null,       // Door data to get section widths
  nSections = 1,         // Number of sections
  gap = 2                // Gap between elements
) {
  console.log('drawArchGridShape called with:', { w, hRect, archHeight, colNumber, nSections });
  const rX = w / 2;
  const rY = archHeight;

  const left = initX;
  const right = initX + w;
  const centerX = initX + rX;
  const centerY = initY + rY;
  const bottom = initY + archHeight + hRect;

  const fw = frameWidth;

  // ====== Glass Fill (Base Layer) ======
  let baseFill = color;
  let usePattern = false;
  let tintOverlay = null;

  // TINTED GLASS
  if (glassType === 'Tinted') {
    baseFill = 'white'; // base is solid white, then tint overlays
    switch (glassDesign?.toLowerCase()) {
      case 'grey':
        tintOverlay = 'rgba(60, 60, 60, 0.2)';
        break;
      case 'bronze':
        tintOverlay = 'rgba(90, 50, 0, 0.2)';
        break;
      default:
        console.warn('Unknown tinted glass design:', glassDesign);
    }
  }

  // TEXTURED GLASS
  if (glassType === 'Textured' && glassDesign) {
    const patternId = `${glassDesign.toLowerCase().replace(/\s/g, '')}Pattern`;
    baseFill = `url(#${patternId})`;
    usePattern = true;
    defineTexturedGlassPatterns(svg, glassDesign);
    //defineTexturedGlassPatterns(svg, 'Rain blast');
  }

  // === Outer path (frame) ===
  const pathOuter = `
    M ${left - fw},${bottom + fw}
    L ${left - fw},${centerY}
    A ${rX + fw},${rY + fw} 0 0 1 ${right + fw},${centerY}
    L ${right + fw},${bottom + fw}
    Z
  `;

  svg.append('path')
    .attr('d', pathOuter)
    .attr('fill', frameColor)
    .attr('stroke', lineFrameColor)
    .attr('stroke-width', 1);

  // === Inner path (shape fill) ===
  let pathInner;
  if (hRect > 0) {
    // Arch + rectangle
    pathInner = `
      M ${left},${bottom}
      L ${left},${centerY}
      A ${rX},${rY} 0 0 1 ${right},${centerY}
      L ${right},${bottom}
      Z
    `;
  } else {
    // Arch only
    pathInner = `
      M ${left},${centerY}
      A ${rX},${rY} 0 0 1 ${right},${centerY}
      L ${right},${centerY}
      L ${left},${centerY}
      Z
    `;
  }

  // === Base glass fill (pattern or solid white or default color) ===
  svg.append('path')
    .attr('d', pathInner)
    .attr('fill', baseFill)
    .attr('stroke', 'none');

  // === Tint overlay (semi-transparent) ===
  if (tintOverlay) {
    svg.append('path')
      .attr('d', pathInner)
      .attr('fill', tintOverlay)
      .attr('stroke', 'none');
  }

  // === Top stroke outline ===
  svg.append('path')
    .attr('d', pathInner)
    .attr('fill', 'none')
    .attr('stroke', outerStrokeColor)
    .attr('stroke-width', outerStrokeWidth);

  // === Optional inner rectangular top line ===
  if (includeInnerTopLine && hRect > 0) {
    svg.append('rect')
      .attr('x', left - frameWidth)
      .attr('y', centerY)
      .attr('width', w + 2 * frameWidth)
      .attr('height', frameWidth)
      .attr('fill', frameColor)
      .attr('stroke', lineFrameColor)
      .attr('stroke-width', 1);
  }

  // === Vertical dividers ===
  // Draw trapezoid dividers between sections that follow the arch curve
  if (nSections > 1 && doorData) {
    let X = left;

    for (let i = 1; i < nSections; i++) {
      const key = `windowWidth${i}_d`;
      const width = parseFloat(doorData?.frameData?.[key]) || 50;

      // Calculate offset X
      X = X + width;

      // Draw first divider stripe (trapezoid)
      const x1Left = X + 1;
      const x1Right = X + frameWidth ;

      // Calculate Y positions at left and right edges of the stripe
      const dx1Left = x1Left - centerX;
      const term1Left = 1 - (dx1Left * dx1Left) / (rX * rX);

      const dx1Right = x1Right - centerX;
      const term1Right = 1 - (dx1Right * dx1Right) / (rX * rX);

      if (term1Left >= 0 && term1Right >= 0) {
        const dy1Left = Math.sqrt(term1Left) * rY;
        const archTopY1Left = centerY - dy1Left;

        const dy1Right = Math.sqrt(term1Right) * rY;
        const archTopY1Right = centerY - dy1Right;

        // Draw trapezoid fill (no stroke)
        const points = 
        `${x1Left},${archTopY1Left} ${x1Right},${archTopY1Right} ${x1Right},${bottom} ${x1Left},${bottom}`;

        svg.append('polygon')
          .attr('points', points)
          .attr('fill', frameColor)
          .attr('stroke', 'none');

        // Draw gap trapezoid between first and second stripe - touches outer arch border
        const gapLeft = x1Left + frameWidth;
        const gapRight = gapLeft + gap + 1;

        // Calculate Y positions on OUTER arch curve (rX + fw, rY + fw)
        const outerRX = rX + frameWidth;
        const outerRY = rY + frameWidth;
        const outerBottom = bottom + frameWidth;

        const dxGapLeft = gapLeft - centerX;
        const termGapLeft = 1 - (dxGapLeft * dxGapLeft) / (outerRX * outerRX);

        const dxGapRight = gapRight - centerX;
        const termGapRight = 1 - (dxGapRight * dxGapRight) / (outerRX * outerRX);

        if (termGapLeft >= 0 && termGapRight >= 0) {
          const dyGapLeft = Math.sqrt(termGapLeft) * outerRY;
          const outerArchTopYGapLeft = centerY - dyGapLeft;

          const dyGapRight = Math.sqrt(termGapRight) * outerRY;
          const outerArchTopYGapRight = centerY - dyGapRight;

          const gapPoints = `${gapLeft},${outerArchTopYGapLeft} ${gapRight},${outerArchTopYGapRight} ${gapRight},${outerBottom} ${gapLeft},${outerBottom}`;

          svg.append('polygon')
            .attr('points', gapPoints)
            .attr('fill', 'white')
            .attr('stroke', 'none');

          // Draw left edge stroke of gap
          svg.append('line')
            .attr('x1', gapLeft)
            .attr('y1', outerArchTopYGapLeft)
            .attr('x2', gapLeft)
            .attr('y2', outerBottom)
            .attr('stroke', lineFrameColor)
            .attr('stroke-width', 1);

          // Draw right edge stroke of gap
          svg.append('line')
            .attr('x1', gapRight)
            .attr('y1', outerArchTopYGapRight)
            .attr('x2', gapRight)
            .attr('y2', outerBottom)
            .attr('stroke', lineFrameColor)
            .attr('stroke-width', 1);
        }

        // Draw left edge stroke
        svg.append('line')
          .attr('x1', x1Left)
          .attr('y1', archTopY1Left)
          .attr('x2', x1Left)
          .attr('y2', bottom)
          .attr('stroke', lineFrameColor)
          .attr('stroke-width', 1);

        // Draw right edge stroke
        /*svg.append('line')
          .attr('x1', x1Right)
          .attr('y1', archTopY1Right)
          .attr('x2', x1Right)
          .attr('y2', bottom)
          .attr('stroke', lineFrameColor)
          .attr('stroke-width', 1);*/
      }

      // Draw second divider stripe (trapezoid)
      const x2Left = X + frameWidth + gap + 2;
      const x2Right = x2Left + frameWidth - 1;

      const dx2Left = x2Left - centerX;
      const term2Left = 1 - (dx2Left * dx2Left) / (rX * rX);

      const dx2Right = x2Right - centerX;
      const term2Right = 1 - (dx2Right * dx2Right) / (rX * rX);

      if (term2Left >= 0 && term2Right >= 0) {
        const dy2Left = Math.sqrt(term2Left) * rY;
        const archTopY2Left = centerY - dy2Left;

        const dy2Right = Math.sqrt(term2Right) * rY;
        const archTopY2Right = centerY - dy2Right;

        // Draw trapezoid fill (no stroke)
        const points = `${x2Left},${archTopY2Left} ${x2Right},${archTopY2Right} ${x2Right},${bottom} ${x2Left},${bottom}`;

        svg.append('polygon')
          .attr('points', points)
          .attr('fill', frameColor)
          .attr('stroke', 'none');

        // Draw left edge stroke
        svg.append('line')
          .attr('x1', x2Left)
          .attr('y1', archTopY2Left)
          .attr('x2', x2Left)
          .attr('y2', bottom)
          .attr('stroke', lineFrameColor)
          .attr('stroke-width', 1);
          
        // Draw right edge stroke
        svg.append('line')
          .attr('x1', x2Right)
          .attr('y1', archTopY2Right)
          .attr('x2', x2Right)
          .attr('y2', bottom)
          .attr('stroke', lineFrameColor)
          .attr('stroke-width', 1);
      }

      // Move X position for next iteration
      X = X + 2 * (frameWidth + gap);
    }
  }
}
